import { useCallback, useMemo } from 'react';
import { TAssignOrder, TCommissionTeam } from '../../../types';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getAttributedOrders, getOrdersByLocation } from '../../../store/orders/selectors';
import { getCommissionTeams } from '../../../store/commissions/selectors';
import { useCommon } from '../../../hooks/useCommon';
import { filterTeamOrders, filterUserOrders } from '../../../utils/helpers/orders.helper';

export function useTeamCommissions() {
  const orders = useAppSelector(getAttributedOrders);
  const commissionTeams = useAppSelector(getCommissionTeams);
  const ordersByLocation = useAppSelector(getOrdersByLocation);
  const { locationsWithColors } = useCommon();

  const getRelevantOrders = useCallback(
    (team: TCommissionTeam) => {
      const { commissionModel } = team;

      if (commissionModel?.goalsBase === 'location' && commissionModel?.baseLocation) {
        return ordersByLocation[commissionModel.baseLocation?.id] || [];
      }

      return filterTeamOrders(orders, team);
    },
    [orders, ordersByLocation],
  );

  const calculateTeamStepsCommission = (team: TCommissionTeam, teamOrders:Array<TAssignOrder>, teamNetSales: number) => {
    const commissionSteps = team.commissionPlan?.commissionSteps || [];
    const employeeCommissions: { [key: string]: number } = {};

    team.members.forEach((member) => {
      const memberOrders = filterUserOrders(teamOrders, member);

      const memberNetSales = memberOrders.reduce((sum, order) => sum + order.summary.netSales, 0);

      let memberCommission = 0;

      // Calculate for the first step
      if (commissionSteps[0]) {
        const firstStepAmount = commissionSteps[0]?.amount;
        const firstStepFrom = (commissionSteps[0]?.from || 0) as number;
        const firstStepPercent = typeof commissionSteps[0]?.percent === 'string'
          ? parseFloat(commissionSteps[0]?.percent)
          : commissionSteps[0]?.percent;

        if (memberNetSales >= firstStepFrom) {
          if (commissionSteps[0]?.isPercentage) {
            memberCommission += (memberNetSales / 100) * firstStepPercent;
          } else if (firstStepAmount) {
            memberCommission += typeof firstStepAmount === 'string' ? parseFloat(firstStepAmount) : firstStepAmount;
          }
        }
      }

      employeeCommissions[member.email] = member.email ? memberCommission : 0;
    });

    commissionSteps.forEach((step, index) => {
      if (index === 0) return;

      const stepFrom = (step.from || 0) as number;
      const stepPercent = typeof step.percent === 'string' ? parseFloat(step.percent) : step.percent;
      const stepAmount = typeof step.amount === 'string' ? parseFloat(step.amount) : step.amount;

      if (teamNetSales >= stepFrom) {
        team.members.forEach((member) => {
          const memberOrders = filterUserOrders(teamOrders, member);

          const memberNetSales = memberOrders.reduce((sum, order) => sum + order.summary.netSales, 0);

          if (step.isPercentage && stepPercent) {
            employeeCommissions[member.email] += (memberNetSales / 100) * stepPercent;
          } else if (stepAmount) {
            employeeCommissions[member.email] += stepAmount;
          }
        });
      }
    });

    return {
      netSales: teamNetSales,
      commission: 0,
      employeeCommissions,
    };
  };

  const calculateSplitCommission = (team: TCommissionTeam, teamOrders:Array<TAssignOrder>, teamNetSales: number) => {
    const totalCommission = teamOrders.reduce((sum, order) => sum + parseFloat((order.commission as string) || '0'), 0);
    const perMemberCommission = totalCommission / (team.members.length || 1);

    const employeeCommissions = team.members.reduce((acc, member) => {
      acc[member.email] = perMemberCommission;
      return acc;
    }, {} as { [key: string]: number });

    return {
      netSales: teamNetSales,
      commission: totalCommission,
      employeeCommissions,
    };
  };

  const calculateDuplicateCommission = (teamOrders: Array<TAssignOrder>, teamNetSales: number) => {
    const totalCommission = teamOrders.reduce((sum, order) => sum + parseFloat((order.commission as string) || '0'), 0);

    return {
      netSales: teamNetSales,
      commission: totalCommission,
      employeeCommissions: {},
    };
  };

  const aggregateTeamOrders = useMemo(() => commissionTeams.flatMap(
    (team) => getRelevantOrders(team),
  ), [commissionTeams, getRelevantOrders]);

  const locationNamesById = useMemo(
    () => Object.fromEntries(locationsWithColors.map((location) => [
      location.id,
      { name: location.name, color: location.color },
    ])),
    [locationsWithColors],
  );

  return {
    calculateTeamStepsCommission,
    calculateSplitCommission,
    calculateDuplicateCommission,
    aggregateTeamOrders,
    locationNamesById,
  };
}
